/// <amd-module name="VSS/Diagnostics/Dialog/DependencyDiagram" />
import * as d3 from "d3";

export function drawDependencyDiagram(element: HTMLElement, names: string[], sizes: number[], connections: [string, string][]): void {
    let svg: d3.Selection<d3.BaseType, d3.Chords, null, undefined>;

    function draw() {
        // Clear current content
        while (element.firstChild) {
            element.removeChild(element.firstChild);
        }

        // Build connection matrix
        const matrix: number[][] = [];
        const nL: number = names.length;
        for (let x = 0; x < nL; ++x) {
            const row: number[] = [];
            let hasConnection = false;
            for (let y = 0; y < nL; ++y) {
                if (connections.some(([source, target]) => source === names[x] && target === names[y])) {
                    row.push(1);
                    hasConnection = true;
                } else {
                    row.push(0);
                }
            }

            if (!hasConnection) {
                // If there are no dependencies, module might have been pulled in by the page, add reference to itself in that case
                row[x] = 1;
            }

            matrix.push(row);
        }

        // Calculate sizes
        for (let x = 0; x < nL; ++x) {
            const size: number = sizes[x];

            // Get number of connections
            const n = matrix[x].reduce((a, v) => a + v, 0);

            for (let y = 0; y < nL; ++y) {
                if (matrix[x][y] === 1) {
                    matrix[x][y] = size / n;
                }
            }
        }

        const margin = { left: 10, top: 10, right: 10, bottom: 10 };
        const width = Math.max(element.clientWidth, 1000);
        const height = Math.max(element.clientHeight, 1000);
        const innerRadius = Math.min(width, height) / 2 - 300; // 300 to reserve space for names
        const outerRadius = innerRadius * 1.1;
        const opacityDefault = 0.8;

        const colors = d3
            .scaleOrdinal<string>()
            .domain(d3.range(names.length).map(x => x.toString()))
            .range(["#301E1E", "#083E77", "#342350", "#567235", "#8B161C", "#DF7C00"]);

        const chord = d3.chord().padAngle(0.02);

        const arc = d3
            .arc()
            .innerRadius(innerRadius * 1.01)
            .outerRadius(outerRadius);

        const path = d3.ribbon().radius(innerRadius);

        svg = d3
            .select(element)
            .append("svg")
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", "translate(" + (width / 2 + margin.left) + "," + (height / 2 + margin.top) + ")")
            .datum(chord(matrix));

        // Arcs
        const outerArcs = svg
            .selectAll("g.group")
            .data(chords => chords.groups)
            .enter()
            .append("g")
            .attr("class", "group")
            .on("mouseover", fade(0.1))
            .on("mouseout", fade(opacityDefault))
            .on("mousedown", remove());

        // Names
        outerArcs
            .append("path")
            .style("fill", d => colors(d.index.toString()))
            .attr("id", d => "group" + d.index)
            .attr("d", arc as any);

        type ChordGroupWithAngle = d3.ChordGroup & { angle: number };
        outerArcs
            .append("svg:text")
            .each((d: ChordGroupWithAngle) => {
                d.angle = (d.startAngle + d.endAngle) / 2;
            })
            .attr("dy", ".15em")
            .attr("text-anchor", (d: ChordGroupWithAngle) => {
                return d.angle > Math.PI ? "end" : null;
            })
            .attr(
                "transform",
                (d: ChordGroupWithAngle) =>
                    `rotate(${(d.angle * 180) / Math.PI - 90}) translate(${innerRadius + 80}) ${d.angle > Math.PI ? "rotate(180)" : ""}`
            )
            // Only return last parts of contribution id
            .text(d => `${names[d.index].substr(3)} (${(sizes[d.index] / 1000).toFixed(2)}k)`);

        // Connections
        svg.selectAll("path.chord")
            .data(chords => chords)
            .enter()
            .append("path")
            .attr("class", "chord")
            .style("fill", d => colors(d.source.index.toString()))
            .style("opacity", opacityDefault)
            .attr("d", path as any)
            .append("title")
            .text((d: d3.Chord) => names[d.target.index]);
    }

    // Interactivity
    function fade(opacity: number) {
        return (_: unknown, i: number) => {
            const targetedIndexes: number[] = [i];

            svg.selectAll("path.chord")
                .filter((d: d3.Chord) => {
                    if (d.source.index === i) {
                        targetedIndexes.push(d.target.index);
                    }
                    if (d.target.index === i) {
                        targetedIndexes.push(d.target.index);
                    }

                    return d.source.index != i && d.source.value > 0;
                })
                .transition()
                .style("opacity", opacity);

            svg.selectAll("g.group")
                .filter((d: d3.ChordGroup) => {
                    return targetedIndexes.indexOf(d.index) === -1;
                })
                .transition()
                .style("opacity", opacity);
        };
    }

    function remove() {
        return (_: unknown, idx: number) => {
            names.splice(idx, 1);
            sizes.splice(idx, 1);

            draw();
        };
    }

    // Draw diagram
    draw();
}
